import { Component, type ReactNode } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/Button";

/**
 * Contains a crashing page to its own content area. Without it a single
 * render error unmounts the whole tree and every later navigation shows a
 * blank screen until reload. Keyed by pathname in AppShell, so moving to
 * another page resets it.
 */
export class PageErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error) {
    console.error("[page crashed]", error);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="mx-auto max-w-lg py-20 text-center">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-danger/12 text-danger">
          <AlertTriangle className="h-6 w-6" />
        </div>
        <h2 className="mt-5 text-xl font-semibold">This page hit a problem</h2>
        <p className="mt-2 text-sm text-fg-muted">
          The rest of the console still works. Try again, or pick another page from the menu.
        </p>
        <p className="mt-3 break-all font-mono text-xs text-fg-subtle">{this.state.error.message}</p>
        <Button className="mt-6" variant="outline" onClick={() => this.setState({ error: null })}>
          <RotateCcw /> Try again
        </Button>
      </div>
    );
  }
}
